import { useReveal } from "@/hooks/useReveal";
import { cn } from "@/lib/utils";

interface StoryStep {
  eyebrow?: string;
  title: string;
  description: string;
}

interface StickyStoryProps {
  /** The sticky media element (left on desktop) */
  image: string;
  imageAlt: string;
  /** Scrolling narrative blocks (right on desktop) */
  steps: StoryStep[];
  className?: string;
}

const StickyStory = ({ image, imageAlt, steps, className }: StickyStoryProps) => {
  return (
    <div className={cn("grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16", className)}>
      {/* Sticky media column */}
      <div className="relative">
        <div className="md:sticky md:top-24 overflow-hidden">
          <img
            src={image}
            alt={imageAlt}
            className="w-full aspect-[3/4] md:aspect-auto md:h-[70vh] object-cover"
            loading="lazy"
          />
        </div>
      </div>

      {/* Scrolling narrative column */}
      <div className="space-y-16 md:space-y-24 md:py-12">
        {steps.map((step, i) => (
          <StoryStepBlock key={i} step={step} index={i} />
        ))}
      </div>
    </div>
  );
};

const StoryStepBlock = ({ step, index }: { step: StoryStep; index: number }) => {
  const { ref, revealed } = useReveal(0.3);

  return (
    <div
      ref={ref}
      className={cn(
        "transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]",
        revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      )}
      style={{ transitionDelay: `${index * 60}ms` }}
    >
      {step.eyebrow && (
        <span className="font-body text-[10px] uppercase tracking-[0.25em] text-accent mb-3 block">
          {step.eyebrow}
        </span>
      )}
      <h3 className="font-headline text-xl md:text-2xl lg:text-3xl text-foreground mb-4">
        {step.title}
      </h3>
      <p className="text-muted-foreground text-sm md:text-base leading-relaxed max-w-lg">
        {step.description}
      </p>
    </div>
  );
};

export default StickyStory;
